import { useSyncExternalStore, useCallback, useRef, useMemo, useState } from 'react';
import * as filestack from 'filestack-js';
import moment from 'moment';
import {
  assetsMediaContentRetrieve,
  ContentUpload,
  MediaContent,
  MediaContentConfig,
  assetsContentUploadCreate,
  assetsMediaContentConfigCreate,
  assetsContentUploadUrlImportCreate,
  assetsContentUploadUnifiedUploadCreate,
  UnifiedContentUpload,
  ContentUploadCreate
} from '@goldcast/api/content';
import { v6 as uuidv6 } from 'uuid';
import { useMutation } from 'react-query';
import {
  contentUploadState,
  ContentUploadState,
  ContentUploadListItem,
  updateTotalPercentageUploaded,
  updateContentState,
  updateMediaContentConfig,
  setUploadContent,
  handleContentUploadError
} from '@/stores/contentUploadStore';
import { ContentStatesEnum, ContentError, ASSETS_FILE_PATH } from '@/Pages/VideoImportPage/constants';
import { PUSHER_STATE_MESSAGES, PUSHER_STATES } from '@/context/PusherContext/PusherContextConstants';
import { getContentUploadedState } from '@/Pages/VideoImportPage/utils';
import { showErrorToast } from '@/libs/toast/toast';
import {
  getContentStoreConfig,
  handleBeforeUnload,
  getFilesValidationErrors
} from '@/Pages/Sessions/NewContentModal/util';
import { REGEX_URL } from '@/Pages/Sessions/NewContentModal/constants';
import { currentUser } from '@/stores/user';
import { getEnvConfig } from '@/constants';
import { getFileKey, getFileNameTitleCase, isVideoFile } from '@/libs/file';
import { getLocalFtuxConfig } from '@/Pages/VideoImportPage/localFtuxConfig.util';
import { getTotalVideoDuration } from '@/libs/getVideoDuration';
import { roundToNDecimalPlaces } from '@/player-config/src/utils/mathUtils';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { loadOrgUsageSummary } from '@/stores/orgUsageSummary';
import { useAppContext } from '@/context/AppContext/AppContext';
import useAnalytics from '@/hooks/useAnalytics';
import useFreeTrialHook from '@/hooks/useFreeTrialHook';
import { FilestackUploadToken } from '@/Pages/VideoImportPage/types';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { core } from '@/stores/core';
import { isScreenShareDetectionFeatureFlagEnabled, isFullVideoFaceRecEnabled } from '@/stores/clip';

const uploadTokens: { [key: string]: FilestackUploadToken } = {};
const isUploadCancelled: { [key: string]: boolean } = {};

export default function useContentUpload(onUploadSuccess?: (videoAssetId: string) => void, workspaceId?: string) {
  const ContentUploadStore = useSyncExternalStore(contentUploadState.subscribe, contentUploadState.getSnapshot);
  const client = filestack.init(getEnvConfig('FILESTACK_API_KEY'));
  const filesIds = useRef({});
  const isCreatingMediaContentConfig = useRef(false);
  const { trackVideoUpload } = useAnalytics();
  const { incrementDurationUsed, checkVideoUploadLimitReached, notifyUserToUpgrade } = useFreeTrialHook();
  const { logger } = useAppContext();

  // URL import related states
  const [urlInputValue, setUrlInputValue] = useState('');
  const [isValidLink, setIsValidLink] = useState(true);

  const initUploadedContent = useCallback(
    ({ mediaContent, uploadData: content }: { mediaContent: MediaContent; uploadData: ContentUpload }) => {
      const { contentState, error } = getContentUploadedState(mediaContent);

      const contentDetails: ContentUploadListItem = {
        projectId: content.project_id ?? '',
        contentId: content.id ?? '',
        contentUrl: content?.import_url,
        title: content.title ?? '',
        av_type: content.av_type as string,
        contentState: contentState,
        error: error,
        videoProcessingTime: mediaContent.video_processing_estimated_time,
        contentUploadedTime: mediaContent.end_time,
        clipGenerationEstimatedTime: mediaContent.clip_generation_estimated_time
      };
      setUploadContent(contentDetails);
      return contentDetails;
    },
    []
  );

  const getUploadedMediaContent = useCallback(
    async (contentId: string) => {
      //eslint-disable-next-line no-useless-catch
      try {
        const mediaContent = await assetsMediaContentRetrieve({ id: contentId });
        const contentUploaded = ContentUploadStore.contentUploadList[contentId];

        contentUploadState.update((state: ContentUploadState) => {
          state.contentUploadList[contentId].contentUploadedTime = mediaContent.end_time;
          state.contentUploadList[contentId].videoProcessingTime = mediaContent.video_processing_estimated_time;
          state.contentUploadList[contentId].clipGenerationEstimatedTime = mediaContent.clip_generation_estimated_time;
        });
        return contentUploaded;
      } catch (error) {
        throw error;
      }
    },
    [ContentUploadStore]
  );

  const handleImportUrlContent = useCallback(
    (contentDetails: ContentUploadListItem) => {
      setUploadContent(contentDetails);
      getUploadedMediaContent(contentDetails.contentId);
    },
    [getUploadedMediaContent]
  );

  const getContentUploadById = useCallback(
    (contentId: string | undefined) => {
      const contentUploaded = contentId && ContentUploadStore.contentUploadList[contentId];
      if (contentUploaded) {
        return ContentUploadStore.contentUploadList[contentId];
      }
      return {
        projectId: '',
        contentId: '',
        contentState: ContentStatesEnum.Uploading,
        file: undefined,
        av_type: '',
        title: '',
        totalPercentageUploaded: 0
      };
    },
    [ContentUploadStore.contentUploadList]
  );

  const UPLOAD_CONFIG = useMemo(
    () => ({
      retry: 3,
      timeout: 60000,
      onProgress: (progress: { totalPercent: number }, contentId: string) => {
        updateTotalPercentageUploaded(progress.totalPercent, contentId);
      }
    }),
    []
  );

  const fetchAndUpdateMediaContent = useCallback(
    async (uploadId: string): Promise<MediaContent | null> => {
      try {
        const mediaContent: MediaContent = await assetsMediaContentRetrieve({ id: uploadId });
        core.update(draft => {
          draft.content = mediaContent;
        });
        return mediaContent;
      } catch (error) {
        logger.error('Error fetching media content:', error);
        return null;
      }
    },
    [logger]
  );

  const changeContentUploadStatus = useCallback(
    async (data, type: ContentStatesEnum, contentId: string) => {
      if (data.upload_id !== contentId) {
        return;
      }

      const screenShareDetectionStatusNotProcessingStates = [
        PUSHER_STATES.SCREENSHARE_DETECTION_DONE,
        PUSHER_STATES.SCREENSHARE_DETECTION_FAILED,
        PUSHER_STATES.SCREENSHARE_DETECTION_UNAVAILABLE
      ];

      if (isScreenShareDetectionFeatureFlagEnabled()) {
        const ftuxDoneState = isFullVideoFaceRecEnabled()
          ? PUSHER_STATES.FTUX_FULLVIDEO_FACIAL_RECOGNITION_DONE
          : PUSHER_STATES.FTUX_CLIPS_FACIAL_RECOGNITION_DONE;

        if (data.state === ftuxDoneState) {
          const mediaContent = await fetchAndUpdateMediaContent(data.upload_id);
          if (!mediaContent) {
            return;
          }

          const screenshareCompletionStates = ['DONE', 'FAILED', 'UNAVAILABLE'];
          const screenshareStatus = mediaContent.screenshare_detection_status;

          if (screenshareStatus && !screenshareCompletionStates.includes(screenshareStatus)) {
            return;
          }
        } else if (screenShareDetectionStatusNotProcessingStates.includes(data.state)) {
          const mediaContent = await fetchAndUpdateMediaContent(data.upload_id);
          if (!mediaContent) {
            return;
          }

          const ftuxStatus = mediaContent.ftux_facial_recognition_status;

          if (ftuxStatus && ftuxStatus !== 'DONE') {
            return;
          }
        }
      }

      const isScreenshareState = screenShareDetectionStatusNotProcessingStates.includes(data.state);

      if (isScreenshareState && !isScreenShareDetectionFeatureFlagEnabled()) {
        return;
      }

      const generationDoneState =
        data.media_type === 'AUDIO'
          ? PUSHER_STATES.CLIP_GENERATION_DONE
          : isFullVideoFaceRecEnabled()
            ? PUSHER_STATES.FTUX_FULLVIDEO_FACIAL_RECOGNITION_DONE
            : PUSHER_STATES.FTUX_CLIPS_FACIAL_RECOGNITION_DONE;

      if (data.state === generationDoneState || data.state === PUSHER_STATES.TRANSCRIPTION_DONE || isScreenshareState) {
        let currentState = ContentStatesEnum.GeneratingCompleted;
        if (type === ContentStatesEnum.Processing) {
          currentState = ContentStatesEnum.Generating;
        } else if (type === ContentStatesEnum.Generating) {
          currentState = ContentStatesEnum.GeneratingCompleted;
        } else if (type === ContentStatesEnum.GeneratingCompleted) {
          currentState = ContentStatesEnum.ContentReadyForPreview;
        }

        updateContentState(currentState, contentId);
        return;
      }

      if (data.state === PUSHER_STATES.CLIP_GENERATION_LIMIT_REACHED) {
        showErrorToast(PUSHER_STATE_MESSAGES[data.state]);
        updateContentState(ContentStatesEnum.ContentReadyForPreview, contentId);
      }

      let errorType: ContentError;

      if (data.state === PUSHER_STATES.TRANSCRIPTION_SUPPORT_RESTRICTED) {
        errorType = ContentError.TRANSCRIPTION_SUPPORT_RESTRICTED;
      } else if (type === ContentStatesEnum.Processing) {
        errorType = ContentError.PROCESSING_FAILED;
      } else {
        errorType = ContentError.GENERATING_FAILED;
      }

      handleContentUploadError(
        {
          type: errorType,
          message: PUSHER_STATE_MESSAGES[data.state]
        },
        contentId
      );
    },
    [fetchAndUpdateMediaContent]
  );

  const trackFilestackError = useCallback(
    filestackError => {
      trackVideoUpload({
        uploadType: 'Upload',
        status: 'Failed',
        failureReason: `${filestackError?.message || ''} ${filestackError?.details?.data?.error}`
      });
    },
    [trackVideoUpload]
  );

  const generateUuids = useCallback((selectedFile, projectId, contentId, duration) => {
    if (!selectedFile) {
      return;
    }
    filesIds.current[getFileKey(selectedFile.name)] = {
      ...filesIds.current[getFileKey(selectedFile.name)],
      projectId,
      contentId,
      duration
    };
  }, []);

  const uploadFileContentUsingFileStack = useCallback(
    (selectedFile, contentId) => {
      if (!selectedFile) {
        return;
      }
      uploadTokens[contentId] = {};

      const configWithContentId = {
        ...UPLOAD_CONFIG,
        onProgress: progress => {
          updateTotalPercentageUploaded(progress.totalPercent, contentId);
        }
      };

      return client.upload(
        selectedFile,
        configWithContentId,
        getContentStoreConfig(ASSETS_FILE_PATH, filesIds.current),
        uploadTokens[contentId]
      );
    },
    [UPLOAD_CONFIG, client]
  );

  const storeFileDetails = useCallback(
    (contentId, projectId, title, av_type, duration) => {
      const createContent = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Uploads_In_VL]
        ? assetsContentUploadUnifiedUploadCreate
        : assetsContentUploadCreate;
      return createContent({
        body: {
          id: contentId,
          project_id: projectId,
          broadcast_type: 'SIMULIVE',
          title: title,
          media_source_type: 'UPLOAD',
          av_type: av_type,
          recording_date: moment().format('YYYY-MM-DD'),
          organization: currentUser.getSnapshot()?.organization as string,
          duration: duration ? duration.toString() : '0',
          ...(workspaceId &&
            featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_workspace_filters] && { workspace_id: workspaceId })
        } as UnifiedContentUpload & ContentUploadCreate & { workspace_id?: string }
      }).catch(() => {
        trackVideoUpload({
          uploadType: 'Upload',
          status: 'Failed',
          failureReason: 'Error upload to API'
        });
        throw new Error('Error upload to API');
      });
    },
    [trackVideoUpload, workspaceId]
  );

  const createMediaContentConfig = useCallback((contentId: string) => {
    if (isCreatingMediaContentConfig.current) {
      return;
    }
    isCreatingMediaContentConfig.current = true;
    assetsMediaContentConfigCreate({
      body: {
        upload: contentId,
        ftux_config: getLocalFtuxConfig(contentId) || {}
      } as MediaContentConfig
    })
      .then(res => {
        updateMediaContentConfig(res);
      })
      .catch(err => {
        logger.error('Error creating media content config: ', err);
      })
      .finally(() => {
        isCreatingMediaContentConfig.current = false;
      });
  }, []);

  const onUploadFailed = useCallback((reason: string, contentId: string) => {
    handleContentUploadError({ type: ContentError.UPLOAD_FAILED, message: reason }, contentId);
  }, []);

  const cancelUpload = useCallback((contentId: string) => {
    if (window.confirm('Are you sure? Aborting upload will discard all changes added during this upload.')) {
      isUploadCancelled[contentId] = true;
      if (uploadTokens[contentId]?.cancel) {
        uploadTokens[contentId]?.cancel?.();
        delete uploadTokens[contentId];
      }
      updateContentState(ContentStatesEnum.Cancelled, contentId);
      return true;
    }
    return false;
  }, []);

  const uploadContent = useCallback(
    async contentUpload => {
      const { file: selectedFile, projectId, contentId, title, av_type, duration } = contentUpload;
      generateUuids(selectedFile, projectId, contentId, duration);
      const originalOnBeforUnload = window.onbeforeunload;

      try {
        window.onbeforeunload = handleBeforeUnload;
        trackVideoUpload({
          uploadType: 'Upload',
          status: 'Started'
        });

        const uploadedContent = await uploadFileContentUsingFileStack(selectedFile, contentId);

        if (uploadedContent.status !== 'Stored') {
          onUploadFailed('Failed to upload to Filestack', contentId);
        } else {
          const response = await storeFileDetails(contentId, projectId, title, av_type, duration);
          createMediaContentConfig(contentId);
          incrementDurationUsed(Number(response.duration));
          window.onbeforeunload = originalOnBeforUnload;

          trackVideoUpload({
            uploadType: 'Upload',
            status: 'Completed'
          });

          await getUploadedMediaContent(contentId);
          updateContentState(ContentStatesEnum.Processing, contentId);
          return response;
        }
      } catch (error: any) {
        logger.error('Upload Error', error);
        window.onbeforeunload = originalOnBeforUnload;
        if (isUploadCancelled[contentId]) {
          isUploadCancelled[contentId] = false;
        } else {
          onUploadFailed(
            error.message ||
              'Oops! Something went wrong with the upload. Please try again. If the issue persists, contact support for assistance',
            contentId
          );
        }
      }
    },
    [
      createMediaContentConfig,
      generateUuids,
      getUploadedMediaContent,
      incrementDurationUsed,
      logger,
      onUploadFailed,
      storeFileDetails,
      trackVideoUpload,
      uploadFileContentUsingFileStack
    ]
  );

  // Functions from useUploadContent
  const handleFilesValidation = useCallback(
    (files: File[]) => {
      const fileName = files[0].name;
      const { files: fileError, global } = getFilesValidationErrors(files);
      const error = fileError[fileName] || (global && global[0]);
      if (error) {
        showErrorToast(error);
        trackVideoUpload({
          uploadType: 'Upload',
          status: 'Disabled',
          url: urlInputValue,
          failureReason: `${error}`
        });
        return true;
      }
      return false;
    },
    [urlInputValue, trackVideoUpload]
  );

  const handleContentUpload = useCallback(
    async (filesToUpload: File[]) => {
      const duration = await getTotalVideoDuration(filesToUpload);
      const contentDetails = {
        projectId: uuidv6({ msecs: new Date().getTime() }),
        contentId: uuidv6({ msecs: new Date().getTime() }),
        duration: roundToNDecimalPlaces(duration, 3),
        file: filesToUpload[0],
        title: getFileNameTitleCase(filesToUpload[0].name),
        av_type: isVideoFile(filesToUpload[0]) ? 'VIDEO' : 'AUDIO',
        contentState: ContentStatesEnum.Uploading
      };
      setUploadContent(contentDetails);
      trackVideoUpload({
        uploadType: 'Upload',
        status: 'Started'
      });
      EventBus.dispatch(CustomEvents.UploadContentStarted, {
        projectId: contentDetails.projectId,
        contentId: contentDetails.contentId
      });
      return contentDetails;
    },
    [trackVideoUpload]
  );

  const onFilesUpload = useCallback(
    async (files: File[]) => {
      const { value, reason } = await checkVideoUploadLimitReached(files);
      if (value) {
        trackVideoUpload({
          uploadType: 'Upload',
          status: 'Disabled',
          url: urlInputValue,
          failureReason: reason
        });
        return;
      }
      const isFileInvalid = handleFilesValidation(files);
      if (!isFileInvalid) {
        return handleContentUpload(files);
      }
    },
    [checkVideoUploadLimitReached, handleFilesValidation, handleContentUpload, trackVideoUpload, urlInputValue]
  );

  function handleUrlInputChange(event: React.ChangeEvent) {
    const value = ((event.target as HTMLInputElement).value || '').trim();
    setUrlInputValue(value);
  }

  function showImportError(err) {
    trackVideoUpload({
      uploadType: 'Import',
      status: 'Failed',
      url: urlInputValue,
      failureReason: err
    });
    try {
      const parsed = JSON.parse(err as string);
      if (parsed && Array.isArray(parsed) && parsed.length > 0 && !!parsed[0]) {
        if (parsed[0].includes('Total content duration usage exceeded')) {
          notifyUserToUpgrade();
          return;
        }
        showErrorToast(parsed[0]);
      }
    } catch (e) {
      logger.warn('Failed parsing import error: ', e);
      showErrorToast(
        `We can't import this video due to issues like an incorrect URL, privacy settings, multiple streams, or technical limitations. Please check and try again.`
      );
    }
  }

  const onImportUrlSuccess = useCallback(
    (data: ContentUpload) => {
      onUploadSuccess?.(data.video_library_id ?? '');
      trackVideoUpload({
        uploadType: 'Import',
        status: 'Completed',
        url: urlInputValue
      });
      handleImportUrlContent({
        projectId: data.project_id ?? '',
        contentId: data.id,
        contentState: ContentStatesEnum.Processing,
        contentUrl: data?.import_url,
        title: data.title ?? '',
        av_type: data.av_type ?? ''
      });
      EventBus.dispatch(CustomEvents.UploadContentStarted, {
        projectId: data.project_id,
        contentId: data.id
      });
      loadOrgUsageSummary(currentUser.getSnapshot()?.organization as string, false);
    },
    [handleImportUrlContent, onUploadSuccess, trackVideoUpload, urlInputValue]
  );

  const { mutate: importFromUrlUnifiedUpload, isLoading: isImportingUrlUnifiedUpload } = useMutation(
    assetsContentUploadUnifiedUploadCreate,
    {
      onError: showImportError,
      onSuccess: onImportUrlSuccess
    }
  );

  const { mutate: importFromURL, isLoading: isImportingUrl } = useMutation(assetsContentUploadUrlImportCreate, {
    onError: showImportError,
    onSuccess: onImportUrlSuccess
  });

  const startImportFromUrl = useCallback(() => {
    const isValid = !!urlInputValue.match(REGEX_URL);
    setIsValidLink(isValid);

    if (!isValid) {
      return;
    }

    if (featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Uploads_In_VL]) {
      importFromUrlUnifiedUpload({
        body: {
          url: urlInputValue,
          ...(workspaceId &&
            featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_workspace_filters] && { workspace_id: workspaceId })
        } as UnifiedContentUpload & { workspace_id?: string }
      });
    } else {
      importFromURL({
        body: {
          url: urlInputValue,
          ...(workspaceId &&
            featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_workspace_filters] && { workspace_id: workspaceId })
        } as any
      });
    }
  }, [urlInputValue, importFromURL, importFromUrlUnifiedUpload, workspaceId]);

  return {
    client,
    contentUploadList: ContentUploadStore.contentUploadList,
    urlInputValue,
    isValidLink,
    isImporting: isImportingUrlUnifiedUpload || isImportingUrl,
    currentMediaContentConfig: ContentUploadStore.currentMediaContentConfig,
    updateContentState,
    updateMediaContentConfig,
    createMediaContentConfig,
    getContentUploadById,
    updateTotalPercentageUploaded,
    initUploadedContent,
    handleContentUploadError,
    changeContentUploadStatus,
    getUploadedMediaContent,
    handleImportUrlContent,
    uploadContent,
    cancelUpload,
    trackFilestackError,
    handleUrlInputChange,
    startImportFromUrl,
    onFilesUpload
  };
}
